import { DEV_MODE } from '../../global/env';
import { ContextConsumerController } from './ContextConsumerController';
import { ContextProviderController } from './ContextProviderController';
/**
 * Creates and returns a pairable context consumer and provider.
 */
export function createContext(initialValue) {
    const id = Symbol('Vidstack.context');
    return {
        id: id,
        initialValue,
        consume(host, options) {
            return new ContextConsumerController(host, initialValue, Object.assign(Object.assign({}, options), { id }));
        },
        provide(host, options) {
            return new ContextProviderController(host, initialValue, Object.assign(Object.assign({}, options), { id }));
        }
    };
}
/**
 * Derives a context from others that was created with `createContext`. This assumes the
 * given `contexts` are ALL provided by the same host element.
 *
 * @param contexts - The contexts to derive values from as it updates.
 * @param derivation - Takes the original context values and outputs the derived value.
 */
export function derivedContext(contexts, derivation) {
    const initialValue = derivation(contexts.map((c) => c.initialValue));
    const derivedContext = createContext(initialValue);
    return {
        id: derivedContext.id,
        initialValue,
        isDerived: true,
        consume: derivedContext.consume,
        provide(host) {
            let connections = 0;
            const values = [];
            const derivedProvider = derivedContext.provide(host);
            contexts.forEach((context, i) => {
                context.consume(host, {
                    onConnect() {
                        connections += 1;
                    },
                    onUpdate(newValue) {
                        values[i] = newValue;
                        if (connections === contexts.length) {
                            derivedProvider.value = derivation(values);
                        }
                    },
                    onDisconnect() {
                        connections -= 1;
                        if (connections === 0)
                            derivedProvider.value = initialValue;
                    }
                });
            });
            return derivedProvider;
        }
    };
}
export function isDerviedContext(context) {
    return !!context.isDerived;
}
/**
 * Takes in a context record which is essentially an object containing 0 or more contexts, and sets
 * the given `host` element as the provider of all the contexts within the given record.
 *
 * @param host
 * @param contextRecord
 */
export function provideContextRecord(host, contextRecord, options) {
    const providerControllers = new Set();
    const providers = {
        __destroy: () => {
            providerControllers.forEach((controller) => controller.stop());
            providerControllers.clear();
        }
    };
    Object.keys(contextRecord).forEach((contextKey) => {
        const context = contextRecord[contextKey];
        const providerOptions = options === null || options === void 0 ? void 0 : options[contextKey];
        const provider = context.provide(host, Object.assign({ 
            /* c8 ignore next */
            name: DEV_MODE && contextKey }, providerOptions));
        providerControllers.add(provider);
        Object.defineProperty(providers, contextKey, {
            enumerable: true,
            configurable: false,
            get() {
                return provider.value;
            },
            set: isDerviedContext(context)
                ? () => {
                    // no-op
                }
                : (newValue) => {
                    provider.value = newValue;
                }
        });
    });
    return providers;
}
